"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useOrganization } from "@clerk/nextjs"
import { toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { DashboardMetadataResource } from "@/types_api/backend/transport/list_dashboard_metadata_response"
import { isDashboardRoute } from "@/utils/isDashboardRoute"

import type { DashboardNavDashboard } from "../navigation/components/DashboardNavPopover"
import { isTransportDashboardQueryLoading } from "../widgets/utils/transportDashboardQueries"

const log = logger("DashboardShellState")

const toDashboardFetchError = (error: unknown): Error | string | undefined => {
  if (error instanceof Error || typeof error === "string") {
    return error
  }

  return undefined
}

const getDashboardIdFromResourceName = (resourceName: string): string => {
  const resourceSegments = resourceName.split("/")
  return resourceSegments[resourceSegments.length - 1] ?? resourceName
}

const mapDashboardMetadataResource = (
  dashboard: DashboardMetadataResource,
): DashboardNavDashboard => ({
  id: getDashboardIdFromResourceName(dashboard.name),
  display_name: dashboard.display_name ?? "",
  is_default: dashboard.is_default,
  is_favorite: dashboard.is_favorite,
})

interface DashboardMetadataListResponse {
  dashboards: DashboardMetadataResource[]
}

export const useDashboardShellState = () => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const { trackEvent } = useAnalytics()
  const dashboardId = router.query.dashboardId as string | undefined
  const isDashboardPage = isDashboardRoute(router.pathname)
  const [currentDashboardId, setCurrentDashboardId] = useState<
    string | undefined
  >(dashboardId)
  const locallySelectedDashboardIdRef = useRef<string | null>(null)

  const organizationSlug = organization?.slug
  const previousOrganizationSlugRef = useRef<string | undefined>(
    organizationSlug,
  )

  const transportDashboardsQuery = useTransportQuery({
    operation: "listDashboardMetadata",
    options: {
      ...CACHE_PROFILES.lookup,
    },
  })
  const dashboards = useMemo(
    () =>
      transportDashboardsQuery.data?.dashboards.map(
        mapDashboardMetadataResource,
      ) ?? [],
    [transportDashboardsQuery.data?.dashboards],
  )
  const dashboardsLoading = isTransportDashboardQueryLoading(
    transportDashboardsQuery,
  )
  const dashboardsError = transportDashboardsQuery.error

  const createDashboard = useTransportMutation("createDashboard")
  const updateDashboard = useTransportMutation("updateDashboard")

  const dashboardListQueryKey = useMemo(
    () => transportQueryUtils.getKey("listDashboardMetadata"),
    [transportQueryUtils],
  )

  useEffect(() => {
    setCurrentDashboardId(dashboardId)
  }, [dashboardId])

  useEffect(() => {
    if (previousOrganizationSlugRef.current === organizationSlug) {
      return
    }

    previousOrganizationSlugRef.current = organizationSlug
    locallySelectedDashboardIdRef.current = null

    if (!dashboardId) {
      setCurrentDashboardId(undefined)
    }
  }, [dashboardId, organizationSlug])

  useEffect(() => {
    if (!isDashboardPage || dashboards.length === 0) {
      return
    }

    if (dashboardId) {
      locallySelectedDashboardIdRef.current = null
      return
    }

    const selectedDashboardExists =
      currentDashboardId != null &&
      dashboards.some((dashboard) => dashboard.id === currentDashboardId)

    if (selectedDashboardExists) {
      locallySelectedDashboardIdRef.current = null
      return
    }

    if (locallySelectedDashboardIdRef.current === currentDashboardId) {
      return
    }

    const defaultDashboard =
      dashboards.find((dashboard) => dashboard.is_default) || dashboards[0]
    setCurrentDashboardId(defaultDashboard.id)
  }, [currentDashboardId, dashboardId, dashboards, isDashboardPage])

  const currentDashboard = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === currentDashboardId),
    [dashboards, currentDashboardId],
  )

  const handleCreateDashboard = useCallback(
    async (displayName: string) => {
      const response = await createDashboard.mutateAsync({
        display_name: displayName,
      })
      const createdDashboard = mapDashboardMetadataResource(response.dashboard)

      queryClient.setQueryData(
        dashboardListQueryKey,
        (existingData: DashboardMetadataListResponse | undefined) => {
          if (!existingData) {
            return existingData
          }

          return {
            dashboards: [...existingData.dashboards, response.dashboard],
          }
        },
      )
      void queryClient.invalidateQueries({ queryKey: dashboardListQueryKey })
      locallySelectedDashboardIdRef.current = createdDashboard.id
      setCurrentDashboardId(createdDashboard.id)

      trackEvent(TRACKING_EVENTS.DASHBOARD.CREATED, {
        dashboard_id: createdDashboard.id,
        dashboard_name: displayName,
      })

      return createdDashboard
    },
    [createDashboard, dashboardListQueryKey, trackEvent, queryClient],
  )

  const handleToggleFavorite = useCallback(
    async (id: string) => {
      const dashboard = dashboards.find(
        (candidateDashboard) => candidateDashboard.id === id,
      )
      if (!dashboard) {
        return
      }

      await queryClient.cancelQueries({ queryKey: dashboardListQueryKey })
      const previousDashboards =
        queryClient.getQueryData<DashboardMetadataListResponse>(
          dashboardListQueryKey,
        )

      queryClient.setQueryData(
        dashboardListQueryKey,
        (existingData: DashboardMetadataListResponse | undefined) => {
          if (!existingData) {
            return existingData
          }

          return {
            dashboards: existingData.dashboards.map((candidateDashboard) =>
              getDashboardIdFromResourceName(candidateDashboard.name) === id
                ? {
                    ...candidateDashboard,
                    is_favorite: !candidateDashboard.is_favorite,
                  }
                : candidateDashboard,
            ),
          }
        },
      )

      try {
        await updateDashboard.mutateAsync({
          dashboard_id: id,
          is_favorite: !dashboard.is_favorite,
        })
        void queryClient.invalidateQueries({ queryKey: dashboardListQueryKey })
      } catch (error) {
        if (previousDashboards) {
          queryClient.setQueryData(dashboardListQueryKey, previousDashboards)
        }
        void queryClient.invalidateQueries({ queryKey: dashboardListQueryKey })
        log.error("Failed to update dashboard favorite", error, {
          dashboardId: id,
        })
        toast.error({
          title: "Could not update favorite",
          error,
          fallback: "Could not update the dashboard favorite.",
        })
      }
    },
    [dashboardListQueryKey, dashboards, queryClient, updateDashboard],
  )

  const handleNewDashboard = useCallback(
    async (displayName = "New Dashboard") => {
      try {
        const res = await handleCreateDashboard(displayName)
        // Navigate to the freshly created dashboard so it's presented
        // immediately instead of leaving the user on the previous one.
        // Prefer the URL slug (always present on the dashboard route) over
        // Clerk's active-org slug, which can be momentarily unavailable.
        const navigationSlug =
          (router.query.slug as string | undefined) ?? organizationSlug
        if (res?.id && navigationSlug) {
          void router.push(
            queryParams.getHref(`/dashboard/${res.id}`, navigationSlug),
          )
        }
      } catch (error) {
        log.error("Failed to create dashboard", error, {
          organizationSlug,
        })
        toast.error({
          title: "Could not create dashboard",
          error,
          fallback: "Could not create the dashboard.",
        })
      }
    },
    [handleCreateDashboard, organizationSlug, queryParams, router],
  )

  return {
    currentDashboard,
    currentDashboardId,
    dashboardFetchError: toDashboardFetchError(dashboardsError),
    dashboards,
    dashboardsLoading,
    handleNewDashboard,
    handleToggleFavorite,
    organizationSlug,
  }
}
